import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { getApi, postApi } from "../../helpers/auth_helpers";
import { persistStudioTokens } from "../../helpers/studioEntitlementsApi";
import notifications from "../../common/Components/Notifications/Notification";
import Loader from "../../common/Components/Loader/Loader";
import { isBillingSyncEnabled } from "../../helpers/billingSyncEnabled";

declare global {
  interface Window {
    Razorpay?: any;
  }
}

const STATUS_COPY: Record<string, string> = {
  TRIAL: "On during your trial.",
  SUBSCRIPTION: "On for your organisation.",
  HASTKALA: "On — enabled for you by HastKala.",
  ON: "On for your organisation.",
  OFF: "Off. Your data for this module is kept.",
};

const loadRazorpay = (): Promise<void> => {
  if (window.Razorpay) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load Razorpay"));
    document.body.appendChild(script);
  });
};

const StudioModulesPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [catalog, setCatalog] = useState<any>(null);
  const [giveUp, setGiveUp] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res: any = await getApi(`${process.env.REACT_APP_SERVERURL}/saas/catalog`);
      if (res?.success) {
        setCatalog(res.data);
      } else {
        notifications({ title: "Error", message: res?.message || "Could not load modules", type: "danger" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const applyTokens = (tokens: any) => {
    if (tokens) {
      persistStudioTokens(tokens);
    }
  };

  const buy = async (moduleId: string) => {
    setSaving(moduleId);
    try {
      const orderRes: any = await postApi(`${process.env.REACT_APP_SERVERURL}/saas/orders`, {
        module: moduleId,
      });
      if (!orderRes?.success) {
        notifications({
          title: "Payment",
          message: orderRes?.message || "Payments are not available. Ask HastKala to enable the module.",
          type: "danger",
        });
        return;
      }
      const order = orderRes.data;
      await loadRazorpay();
      const checkout = new window.Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "HastKalaPramanak",
        description: moduleId,
        order_id: order.orderId,
        handler: async (response: any) => {
          const verify: any = await postApi(`${process.env.REACT_APP_SERVERURL}/saas/payments/verify`, {
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
            module: moduleId,
          });
          if (verify?.success && verify?.data?.tokens) {
            applyTokens(verify.data.tokens);
            notifications({ title: "Module added", message: "This module is on for your organisation.", type: "success" });
            await load();
            window.location.reload();
          } else {
            notifications({ title: "Error", message: verify?.message || "Could not confirm payment", type: "danger" });
          }
        },
      });
      checkout.open();
    } catch (e) {
      notifications({ title: "Error", message: "Could not start payment", type: "danger" });
    } finally {
      setSaving("");
    }
  };

  const setChoice = async (moduleId: string, enabled: boolean) => {
    setSaving(moduleId);
    try {
      const res: any = await postApi(`${process.env.REACT_APP_SERVERURL}/saas/modules/choice`, {
        module: moduleId,
        enabled,
      });
      if (!res?.success) {
        notifications({ title: "Error", message: res?.message || "Could not update module", type: "danger" });
        return;
      }
      applyTokens(res?.data?.tokens);
      if (enabled && res?.data?.needsPayment) {
        await buy(moduleId);
        return;
      }
      notifications({
        title: enabled ? "Module added" : "Module turned off",
        message: enabled
          ? "This module is on for your organisation."
          : "It is off. Product and certificate data is kept.",
        type: "success",
      });
      await load();
      window.location.reload();
    } catch (e) {
      notifications({ title: "Error", message: "Could not update module", type: "danger" });
    } finally {
      setSaving("");
      setGiveUp(null);
    }
  };

  if (loading) {
    return <Loader />;
  }

  const modules = catalog?.modules || [];

  return (
    <div className="studio-modules-page">
      <p className="studio-modules-page__lead">
        Add the Studio tools you need. Turn one off anytime — records stay in place.
        New organisations have every module for {catalog?.trialDays || 7} days.
      </p>
      {catalog?.testModeFree ? (
        <p className="studio-modules-page__note">
          Test environment: paid modules switch on free for {catalog?.testModeDays || 30} days, no payment needed.
        </p>
      ) : !catalog?.paymentsConfigured ? (
        <p className="studio-modules-page__note">
          Online payment is not configured here. You can still turn trial modules off, or ask HastKala to enable one.
        </p>
      ) : null}
      <div className="studio-modules-page__list">
        {modules.map((mod: any) => {
          const on = Boolean(mod.entitled);
          const status = mod.status || (on ? "ON" : "OFF");
          return (
            <div key={mod.id} className="studio-modules-page__card">
              <div className="studio-modules-page__card-main">
                <h3>{mod.label}</h3>
                <p>
                  {mod.trialAvailable
                    ? `Free ${catalog?.trialDays || 7}-day trial. It starts when you first open ${mod.label}.`
                    : status === "TRIAL" && mod.moduleTrialEndsAt
                    ? `On during your free trial, until ${new Date(mod.moduleTrialEndsAt).toLocaleDateString()}.`
                    : STATUS_COPY[status] || STATUS_COPY.OFF}
                </p>
              </div>
              <div className="studio-modules-page__card-actions">
                {on && mod.id === "BILLING_SYNC" && isBillingSyncEnabled() ? (
                  <Link to="/settings/zoho-books" className="btn btn-primary">
                    Set up Zoho Books
                  </Link>
                ) : null}
                {on ? (
                  <Button
                    color="secondary"
                    outline
                    disabled={saving === mod.id || !mod.canGiveUp}
                    onClick={() => setGiveUp(mod)}
                  >
                    Turn off
                  </Button>
                ) : mod.trialAvailable && mod.navKey === "karkhana" ? (
                  <Link to="/karkhana" className="btn btn-primary">
                    Start free trial
                  </Link>
                ) : (
                  <Button
                    color="primary"
                    disabled={saving === mod.id || !mod.canTake}
                    onClick={() => setChoice(mod.id, true)}
                  >
                    {catalog?.testModeFree
                      ? "Add module (free on test)"
                      : catalog?.paymentsConfigured && status === "OFF"
                      ? `Add${mod.amountPaise ? ` — ₹${(mod.amountPaise / 100).toFixed(0)}` : ""}`
                      : "Add module"}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Modal isOpen={Boolean(giveUp)} toggle={() => setGiveUp(null)}>
        <ModalHeader toggle={() => setGiveUp(null)}>Turn off {giveUp?.label}?</ModalHeader>
        <ModalBody>
          The module will leave your Studio menu. Products, certificates, and other records are not deleted.
          You can add it again later from this page.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setGiveUp(null)}>
            Keep it
          </Button>
          <Button
            color="primary"
            disabled={saving === giveUp?.id}
            onClick={() => giveUp && setChoice(giveUp.id, false)}
          >
            Turn off
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default StudioModulesPage;
